import { BodyText, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components";
import { trackEvent } from "@/lib/tracking";
import type * as fhirTypes from "fhir/r4";
import { useState } from "react";
import type { z } from "zod";
import { useDeleteDisposition } from "../network/useDeleteDisposition";
import { useUpdateDisposition } from "../network/useUpdateDisposition";
import { DispositionEditor } from "./DispositionEditor";
import type { editDispositionSchema } from "./dispositionSchemas";

interface DispositionProps {
  carePlanId: string;
  encounterId: string;
  disabled?: boolean;
  task: fhirTypes.Task;
}

export function Disposition({
  carePlanId,
  encounterId,
  disabled,
  task,
}: DispositionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const updateDisposition = useUpdateDisposition({
    encounterId,
    carePlanId,
    dispositionId: task.id,
  });

  const deleteDisposition = useDeleteDisposition({
    encounterId,
    carePlanId,
    dispositionId: task.id,
  });

  const onSubmit = async (values: z.infer<typeof editDispositionSchema>) => {
    try {
      await updateDisposition.mutateAsync({
        dispositionCode: values.code,
        dispositionDescription: values.text,
      });
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to update disposition.");
    }
  };

  const onDelete = async () => {
    try {
      await deleteDisposition.mutateAsync();
    } catch (error) {
      toast.error("Failed to delete disposition.");
    }
  };

  if (isEditing) {
    return (
      <DispositionEditor
        onSubmit={onSubmit}
        onCancel={() => {
          trackEvent(TRACKERS.CANCEL_DISPOSITION);
          setIsEditing(false);
        }}
        isLoading={updateDisposition.isPending}
        task={task}
      />
    );
  }

  // what the fuk
  return (
    <EditableRecordButton
      disabled={disabled}
      onClick={() => setIsEditing(true)}
      onDelete={onDelete}
      deletePending={deleteDisposition.isPending}
      deleteTooltipText="Delete disposition"
    >
      <div className="grid grid-cols-[auto_1fr] gap-2">
        <BodyText size="sm" bold className="w-[100px]">
          {task.code?.text || task.code?.coding?.[0].display}
        </BodyText>
        <BodyText
          size="sm"
          textColor={disabled ? "tertiary" : "primary"}
          className="grid grid-cols-[auto_1fr]"
          preserveLineBreaks
        >
          {task.description}
        </BodyText>
      </div>
    </EditableRecordButton>
  );
}
